import { useState, useMemo } from "react";
import { useAllMcqs } from "@/hooks/useAllMcqs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Separator } from "@/components/ui/separator";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  Zap, 
  FileText, 
  Brain, 
  CheckCircle, 
  AlertTriangle,
  Play,
  Pause,
  Download,
  Upload,
  BarChart3
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface BulkOperation {
  id: string;
  name: string;
  description: string;
  type: "quality-check" | "difficulty-analysis" | "tag-update" | "format-normalize" | "translate";
  estimatedTime: string;
  requiresReview: boolean;
}

interface ProcessingJob {
  id: string;
  operation: string;
  totalItems: number;
  processedItems: number;
  status: "running" | "paused" | "completed" | "failed";
  startTime: string;
  errors: string[];
  results: any[];
}

export default function BulkContentProcessor() {
  const { toast } = useToast();
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [selectedOperation, setSelectedOperation] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentJob, setCurrentJob] = useState<ProcessingJob | null>(null);
  const [contentFilter, setContentFilter] = useState("all");

  const availableOperations: BulkOperation[] = [
    {
      id: "quality-check",
      name: "Quality Assessment",
      description: "Analyze content quality and provide improvement suggestions",
      type: "quality-check",
      estimatedTime: "2-3 minutes per 100 items",
      requiresReview: true
    },
    {
      id: "difficulty-analysis",
      name: "Difficulty Analysis",
      description: "Automatically assess and adjust difficulty levels",
      type: "difficulty-analysis",
      estimatedTime: "1-2 minutes per 100 items",
      requiresReview: true
    },
    {
      id: "auto-tagging",
      name: "Smart Tagging",
      description: "Apply intelligent tags based on content analysis",
      type: "tag-update",
      estimatedTime: "1 minute per 100 items",
      requiresReview: false
    },
    {
      id: "format-normalize",
      name: "Format Normalization",
      description: "Standardize formatting and structure across content",
      type: "format-normalize",
      estimatedTime: "30 seconds per 100 items",
      requiresReview: false
    },
    {
      id: "medical-validation",
      name: "Medical Accuracy Check",
      description: "Validate medical information against current guidelines",
      type: "quality-check",
      estimatedTime: "3-5 minutes per 100 items",
      requiresReview: true
    }
  ];

  // Fetch ALL MCQs from the database
  const { data: allMcqs, isLoading: mcqsLoading } = useAllMcqs();
  
  // Transform MCQs to content items format
  const contentItems = useMemo(() => {
    if (!allMcqs) return [];
    
    return allMcqs.map((mcq) => ({
      id: mcq.id,
      type: "MCQ",
      title: mcq.stem.substring(0, 60) + (mcq.stem.length > 60 ? "..." : ""),
      tags: mcq.tags || [],
      lastModified: new Date(mcq.updated_at).toLocaleDateString()
    }));
  }, [allMcqs]);

  const processingHistory = [
    {
      id: "1",
      operation: "Quality Assessment",
      itemsProcessed: 156,
      completedAt: "2 hours ago",
      status: "completed",
      improvements: 23
    },
    {
      id: "2", 
      operation: "Auto-tagging",
      itemsProcessed: 89,
      completedAt: "1 day ago",
      status: "completed",
      improvements: 89
    },
    {
      id: "3",
      operation: "Difficulty Analysis", 
      itemsProcessed: 67,
      completedAt: "3 days ago",
      status: "completed",
      improvements: 12
    }
  ];

  const startBulkProcessing = async () => {
    if (!selectedOperation || selectedItems.length === 0) {
      toast({
        title: "Invalid Selection",
        description: "Please select an operation and at least one content item.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessing(true);
    const operation = availableOperations.find(op => op.id === selectedOperation);
    
    const job: ProcessingJob = {
      id: Date.now().toString(),
      operation: operation?.name || "",
      totalItems: selectedItems.length,
      processedItems: 0,
      status: "running",
      startTime: new Date().toISOString(),
      errors: [],
      results: []
    };

    setCurrentJob(job);

    try {
      const { data, error } = await supabase.functions.invoke('ai-bulk-processor', {
        body: {
          operation: selectedOperation,
          contentIds: selectedItems
        }
      });

      if (error) throw error;

      // Simulate progressive processing
      const interval = setInterval(() => {
        setCurrentJob(prev => {
          if (!prev) return null;
          
          const newProcessed = Math.min(prev.processedItems + 1, prev.totalItems);
          
          if (newProcessed >= prev.totalItems) {
            clearInterval(interval);
            setIsProcessing(false);
            toast({
              title: "Bulk Processing Complete",
              description: `Successfully processed ${newProcessed} items.`,
            });
            return { ...prev, processedItems: newProcessed, status: "completed" };
          }
          
          return { ...prev, processedItems: newProcessed };
        });
      }, 1000);

    } catch (error) {
      console.error('Error starting bulk processing:', error);
      setIsProcessing(false);
      setCurrentJob(prev => prev ? { ...prev, status: "failed" } : null);
      toast({
        title: "Processing Failed",
        description: "Failed to start bulk processing. Please try again.",
        variant: "destructive"
      });
    }
  };

  const pauseProcessing = () => {
    setIsProcessing(false);
    setCurrentJob(prev => prev ? { ...prev, status: "paused" } : null);
    toast({
      title: "Processing Paused",
      description: "Bulk processing has been paused.",
    });
  };

  const toggleItemSelection = (itemId: string) => {
    setSelectedItems(prev => 
      prev.includes(itemId) 
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const selectAllItems = () => {
    setSelectedItems(contentItems.map(item => item.id));
  };

  const clearSelection = () => {
    setSelectedItems([]);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "running": return "text-primary";
      case "completed": return "text-success";
      case "failed": return "text-destructive";
      case "paused": return "text-warning";
      default: return "text-muted-foreground";
    }
  };

  const getOperationIcon = (type: string) => {
    switch (type) {
      case "quality-check": return <CheckCircle className="h-4 w-4" />;
      case "difficulty-analysis": return <BarChart3 className="h-4 w-4" />;
      case "tag-update": return <FileText className="h-4 w-4" />;
      case "format-normalize": return <Zap className="h-4 w-4" />;
      default: return <Brain className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Bulk Content Processor</h3>
          <p className="text-sm text-muted-foreground">
            Process multiple content items efficiently with AI-powered operations
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="h-4 w-4" />
                Select Operation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3">
                {availableOperations.map((operation) => (
                  <div 
                    key={operation.id}
                    className={`p-3 border rounded-lg cursor-pointer transition-colors ${
                      selectedOperation === operation.id 
                        ? "border-primary bg-primary/5" 
                        : "hover:bg-muted/50"
                    }`}
                    onClick={() => setSelectedOperation(operation.id)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-1">
                        {getOperationIcon(operation.type)}
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium">{operation.name}</span>
                          {operation.requiresReview && (
                            <Badge variant="secondary" className="text-xs">
                              Requires Review
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground mb-2">
                          {operation.description}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Est. time: {operation.estimatedTime}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Select Content ({selectedItems.length} / {contentItems.length.toLocaleString()} MCQs)
                </span>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={selectAllItems}
                    disabled={mcqsLoading}
                  >
                    Select All ({contentItems.length})
                  </Button>
                  <Button variant="outline" size="sm" onClick={clearSelection}>
                    Clear
                  </Button>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex gap-4">
                <Select value={contentFilter} onValueChange={setContentFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All MCQs ({contentItems.length})</SelectItem>
                    <SelectItem value="mcq">MCQ Questions</SelectItem>
                    <SelectItem value="note">Study Notes</SelectItem>
                    <SelectItem value="untagged">Untagged Items</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {mcqsLoading ? (
                <div className="text-center py-8 text-muted-foreground">
                  Loading all MCQs...
                </div>
              ) : (
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {contentItems.map((item) => (
                  <div key={item.id} className="flex items-center gap-3 p-3 border rounded-lg">
                    <Checkbox
                      checked={selectedItems.includes(item.id)}
                      onCheckedChange={() => toggleItemSelection(item.id)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium">{item.title}</span>
                        <Badge variant="outline" className="text-xs">
                          {item.type}
                        </Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          {item.tags.map((tag, index) => (
                            <Badge key={index} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <span className="text-xs text-muted-foreground">
                          • {item.lastModified}
                        </span>
                      </div>
                    </div>
                  </div>
                  ))}
                </div>
              )}

              {selectedOperation && selectedItems.length > 0 && (
                <>
                  <Separator />
                  <div className="flex gap-2">
                    {!isProcessing ? (
                      <Button onClick={startBulkProcessing} className="flex items-center gap-2">
                        <Play className="h-4 w-4" />
                        Start Processing
                      </Button>
                    ) : (
                      <Button onClick={pauseProcessing} variant="destructive" className="flex items-center gap-2">
                        <Pause className="h-4 w-4" />
                        Pause
                      </Button>
                    )}
                  </div>
                </>
              )}
            </CardContent>
          </Card>

          {currentJob && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="h-4 w-4" />
                  Processing Status
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="font-medium">{currentJob.operation}</span>
                  <Badge variant={currentJob.status === "completed" ? "default" : "secondary"} 
                         className={getStatusColor(currentJob.status)}>
                    {currentJob.status}
                  </Badge>
                </div>

                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Progress</span>
                    <span>{currentJob.processedItems} / {currentJob.totalItems}</span>
                  </div>
                  <Progress 
                    value={(currentJob.processedItems / currentJob.totalItems) * 100} 
                    className="h-2" 
                  />
                </div>

                <div className="text-sm text-muted-foreground">
                  Started: {new Date(currentJob.startTime).toLocaleString()}
                </div>

                {currentJob.errors.length > 0 && (
                  <Alert>
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription>
                      {currentJob.errors.length} error(s) occurred during processing.
                    </AlertDescription>
                  </Alert>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <div className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <Button variant="outline" className="w-full justify-start" size="sm">
                <Upload className="h-4 w-4 mr-2" />
                Import Content
              </Button>
              <Button variant="outline" className="w-full justify-start" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export Results
              </Button>
              <Button variant="outline" className="w-full justify-start" size="sm">
                <FileText className="h-4 w-4 mr-2" />
                Generate Report
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Processing History
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {processingHistory.map((job) => (
                <div key={job.id} className="p-3 border rounded-lg">
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium text-sm">{job.operation}</span>
                    <Badge variant="outline" className="text-xs">
                      {job.status}
                    </Badge>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-1">
                    <div>{job.itemsProcessed} items processed</div>
                    <div>{job.improvements} improvements made</div>
                    <div>{job.completedAt}</div>
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}